import {useState, useRef, useEffect, useCallback} from 'react';
import { Button } from 'shared/components/buttons';
import Input from 'shared/components/Input';
import SearchableDropdownSelectOptions from 'shared/components/searchable_dropdown_select/SearchableDropdownSelectOptions';
import _ from 'lodash';
import {FormattedMessage} from 'react-intl';

const SearchableDropdownSelect = (
  {
    label,
    optionCategories = [],
    options = [],
    onQueryChange,
    onSelect,
    disabled = false,
    buttonTrait = 'default',
    buttonSize = 'sm',
    className = ''
  }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [dropdownAnimationStatus, setDropdownAnimationStatus] = useState('is-closed');
  const [filteredOptionCategories, setFilteredOptionCategories] = useState(optionCategories);
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const closeTimeoutRef = useRef(null);

  const allOptions = _.flatMap(filteredOptionCategories, optionCategory => optionCategory.options.toJS ? optionCategory.options.toJS() : optionCategory.options);
  const searchable = !_.isEmpty(optionCategories) && allOptions.length > 7 || query.length > 0;

  const setIsOpenWithTimeout = useCallback(open => {
    if (open) {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
        closeTimeoutRef.current = null;
      }
      setIsOpen(true);
      setDropdownAnimationStatus('is-open');
    } else {
      if (closeTimeoutRef.current) return;
      setDropdownAnimationStatus('is-closed');

      closeTimeoutRef.current = setTimeout(() => {
        setIsOpen(false);
      }, 400);
    }
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target) && !event.target.classList.contains('searchable-dropdown-select-close')) {
        setIsOpenWithTimeout(false);
      }
    };

    document.addEventListener('click', handleClickOutside);

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isOpen]);

  const toggleDropdown = useCallback(() => {
    if (disabled) return;

    setIsOpenWithTimeout(!isOpen);
  }, [disabled, isOpen]);

  const handleQueryChange = useCallback((value) => {
    setQuery(value);
    setSelectedIndex(-1);

    setFilteredOptionCategories(
      _.filter(_.map(optionCategories, optionCategory => {
        const filteredOptions = _.filter(optionCategory.options, option => option.name.toLowerCase().includes(value.toLowerCase()));

        if (_.isEmpty(filteredOptions)) return null;

        return {
          name: optionCategory.name,
          alreadyTranslated: optionCategory.alreadyTranslated,
          options: filteredOptions
        };
      }))
    );
  }, [onQueryChange]);

  const handleClear = useCallback(() => {
    handleQueryChange('');
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, [onQueryChange]);

  const handleSelect = useCallback((suggestion) => {
    if (onSelect) {
      onSelect(suggestion);
    }
    setIsOpenWithTimeout(false);
  }, [onSelect]);

  const handleKeyDown = useCallback((e) => {
    if (_.isEmpty(allOptions)) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => Math.min(allOptions.length - 1, prev + 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => Math.max(prev - 1, 0));
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0) {
          handleSelect(allOptions[selectedIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpenWithTimeout(false);
        break;
      default:
        break;
    }
  }, [allOptions, selectedIndex, handleSelect]);

  const handleFocus = useCallback(() => {
    if (inputRef.current) {
      inputRef.current.select();
    }
  }, []);

  return (
    <div ref={containerRef} className={`searchable-dropdown-select ${className}`} onKeyDown={handleKeyDown}>
      <Button
        trait={buttonTrait}
        size={buttonSize}
        onClick={toggleDropdown}
        disabled={disabled}
      >
        <FormattedMessage id={label}/>
        <i className='material-symbols-solid'>{'arrow_drop_down'}</i>
      </Button>
      {isOpen && (
        <div className={`searchable-dropdown-select-list bg-white rounded box-shadow-2 border ${dropdownAnimationStatus}`}>
          {searchable && <div className='p2 bg-white mbn2 sticky top-0'>
            <Input
              type='search'
              value={query}
              inputRef={inputRef}
              onFocus={handleFocus}
              onChange={e => handleQueryChange(e.target.value)}
              className='InputSearch full-width'
              placeholder={'global_search.Search'}
            />
            {query.length > 0 && (
              <i onClick={handleClear} className='searchable-dropdown-select-close material-symbols-solid clickable h3 waterloo absolute'>{'cancel'}</i>
            )}
          </div>}
          <SearchableDropdownSelectOptions
            sections={filteredOptionCategories}
            options={options}
            selectedIndex={selectedIndex}
            query={query}
            onSelect={handleSelect}
          />
        </div>
      )}
    </div>
  );
};

export default SearchableDropdownSelect;
